import { useCallback, useEffect, useState } from "react";
import type { AccessGrant, AccessRole } from "shared";
import { buildShareUrl, sharesApi } from "@/api/shares";
import { useConfirm } from "@/components/feedback/Confirm";
import { useToast } from "@/components/feedback/Toast";
import { Button } from "@/components/primitives/Button";
import { Dialog } from "@/components/primitives/Dialog";

type Props = {
	mapId: string;
	open: boolean;
	onClose: () => void;
};

function roleLabel(role: AccessRole): string {
	return role === "editor" ? "Bearbeiten" : "Ansehen";
}

export function ShareDialog({ mapId, open, onClose }: Props) {
	const { toast } = useToast();
	const confirm = useConfirm();
	const [shares, setShares] = useState<AccessGrant[]>([]);
	const [loading, setLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const [role, setRole] = useState<AccessRole>("viewer");
	const [busyId, setBusyId] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await sharesApi.listForMap(mapId);
			setShares(res.shares);
		} catch (e) {
			toast(e instanceof Error ? e.message : "Freigaben laden fehlgeschlagen", "error");
		} finally {
			setLoading(false);
		}
	}, [mapId, toast]);

	useEffect(() => {
		if (!open) return;
		void load();
	}, [open, load]);

	const createShare = async () => {
		setCreating(true);
		try {
			const { share } = await sharesApi.createForMap(mapId, { role });
			setShares((prev) => [share, ...prev]);
			if (share.token) {
				const url = buildShareUrl(mapId, share.token, share.role);
				try {
					await navigator.clipboard.writeText(url);
					toast("Link erstellt und kopiert", "success");
				} catch {
					toast("Link erstellt", "success");
				}
			} else {
				toast("Freigabe erstellt", "success");
			}
		} catch (e) {
			toast(e instanceof Error ? e.message : "Freigabe fehlgeschlagen", "error");
		} finally {
			setCreating(false);
		}
	};

	const copyLink = async (share: AccessGrant) => {
		if (!share.token) return;
		const url = buildShareUrl(mapId, share.token, share.role);
		try {
			await navigator.clipboard.writeText(url);
			toast("Link kopiert", "success");
		} catch {
			toast("Kopieren fehlgeschlagen", "error");
		}
	};

	const revoke = async (share: AccessGrant) => {
		const ok = await confirm({
			title: "Freigabe widerrufen?",
			description: "Der Link funktioniert danach nicht mehr.",
			confirmLabel: "Widerrufen",
			variant: "danger",
		});
		if (!ok) return;
		setBusyId(share.id);
		try {
			await sharesApi.revoke(share.id);
			setShares((prev) => prev.filter((s) => s.id !== share.id));
			toast("Freigabe widerrufen", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Widerruf fehlgeschlagen", "error");
		} finally {
			setBusyId(null);
		}
	};

	const rotate = async (share: AccessGrant) => {
		const ok = await confirm({
			title: "Token rotieren?",
			description: "Der alte Link wird ungültig, ein neuer wird erstellt.",
			confirmLabel: "Rotieren",
			variant: "danger",
		});
		if (!ok) return;
		setBusyId(share.id);
		try {
			const { share: next } = await sharesApi.rotate(share.id);
			setShares((prev) => [next, ...prev.filter((s) => s.id !== share.id)]);
			if (next.token) {
				const url = buildShareUrl(mapId, next.token, next.role);
				try {
					await navigator.clipboard.writeText(url);
					toast("Neuer Link kopiert", "success");
				} catch {
					toast("Token rotiert", "success");
				}
			}
		} catch (e) {
			toast(e instanceof Error ? e.message : "Rotation fehlgeschlagen", "error");
		} finally {
			setBusyId(null);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title="Karte teilen"
			description="Erzeuge einen Link mit Lesen- oder Bearbeiten-Recht."
		>
			<div className="mt-5 flex flex-wrap items-end gap-3">
				<label className="flex min-w-[10rem] flex-1 flex-col gap-1.5 text-sm">
					<span className="font-medium text-fg">Rolle</span>
					<select
						value={role}
						onChange={(e) => setRole(e.target.value as AccessRole)}
						className="h-9 rounded-[var(--radius-control)] bg-surface px-3 text-sm text-fg ring-1 ring-[var(--ring-hairline)] outline-none focus:ring-2 focus:ring-teal-500"
					>
						<option value="viewer">Ansehen</option>
						<option value="editor">Bearbeiten</option>
					</select>
				</label>
				<Button size="sm" disabled={creating} onClick={() => void createShare()}>
					{creating ? "…" : "Link erzeugen"}
				</Button>
			</div>

			<div className="mt-6">
				<p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
					Aktive Freigaben
				</p>
				{loading ? (
					<p className="text-sm text-fg-muted">Laden…</p>
				) : shares.length === 0 ? (
					<p className="rounded-[var(--radius-control)] bg-surface-raised px-3 py-6 text-center text-sm text-fg-muted">
						Noch keine Freigaben
					</p>
				) : (
					<ul className="max-h-64 space-y-1.5 overflow-y-auto">
						{shares.map((share) => (
							<li
								key={share.id}
								className="rounded-[var(--radius-control)] bg-surface-raised px-3 py-2"
							>
								<div className="flex items-center justify-between gap-2">
									<div className="min-w-0">
										<p className="text-sm font-medium text-fg">
											{roleLabel(share.role)}
											<span className="ml-2 text-xs font-normal text-fg-muted">
												{share.principalType}
											</span>
										</p>
										{share.token ? (
											<p className="truncate font-mono text-xs text-fg-muted">
												…{share.token.slice(-8)}
											</p>
										) : null}
									</div>
									<div className="flex shrink-0 gap-1">
										{share.token ? (
											<>
												<Button
													variant="secondary"
													size="sm"
													disabled={busyId === share.id}
													onClick={() => void copyLink(share)}
												>
													Kopieren
												</Button>
												<Button
													variant="ghost"
													size="sm"
													disabled={busyId === share.id}
													onClick={() => void rotate(share)}
												>
													Rotieren
												</Button>
											</>
										) : null}
										<Button
											variant="danger"
											size="sm"
											disabled={busyId === share.id}
											onClick={() => void revoke(share)}
										>
											Widerrufen
										</Button>
									</div>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		</Dialog>
	);
}
